import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Package, Film, Image as ImageIcon, Mic, Search, LibraryBig } from 'lucide-react';
import { regenApi } from '../lib/api';
import { cached } from '../lib/cache';

import { MediaThumb } from '../components/MediaThumb';

// Stable server-generated poster (never black, no refresh) via the shared MediaThumb.
function AssetMedia({ url, s3key, className }: { url?: string; s3key?: string; className?: string }) {
  return <MediaThumb url={url} s3key={s3key} className={className} />;
}

const MEDIA_TABS = [
  { id: '', label: 'All', icon: Package },
  { id: 'video', label: 'Video', icon: Film },
  { id: 'image', label: 'Image', icon: ImageIcon },
  { id: 'audio', label: 'Audio', icon: Mic },
];
const KINDS = ['', 'avatar', 'map', 'broll', 'hook', 'winner', 'voice'];
const AGE_BANDS = ['', 'under35', '35-44', '45-55', '55plus'];
const GENDERS = ['', 'female', 'male'];

export function AssetLibraryPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [assets, setAssets] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [media, setMedia] = useState('');
  const [kind, setKind] = useState('');
  const [ageBand, setAgeBand] = useState('');
  const [gender, setGender] = useState('');
  const [lipOnly, setLipOnly] = useState(false);

  const PAGE_SIZE = 48;
  const debTimer = useRef<any>(null);

  // debounce the search box so each keystroke doesn't fire a query
  useEffect(() => {
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => debTimer.current && clearTimeout(debTimer.current);
  }, [q]);

  // any filter change resets to page 1
  useEffect(() => { setPage(1); }, [qDebounced, media, kind, ageBand, gender, lipOnly]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      q: qDebounced || undefined,
      media: media || undefined,
      kind: kind || undefined,
      age_band: ageBand || undefined,
      gender: gender || undefined,
      usable_as: lipOnly ? 'avatar_lipsync' : undefined,
      page,
      page_size: PAGE_SIZE,
    };
    // cache each filter/page combo for 3 min → tab switches & back-nav don't re-hit the DB
    cached(`assets:${JSON.stringify(params)}`, 180000, () => regenApi.getAssets(params))
      .then(r => {
        const d = r.data;
        setAssets(d.data || []);
        setTotal(d.total || 0);
        setPages(d.pages || 1);
      })
      .catch(() => { setAssets([]); setTotal(0); setPages(1); })
      .finally(() => setLoading(false));
  }, [qDebounced, media, kind, ageBand, gender, lipOnly, page]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setQ(''); setMedia(''); setKind(''); setAgeBand(''); setGender(''); setLipOnly(false); };
  const activeFilters = [media, kind, ageBand, gender, lipOnly ? 'lip' : '', qDebounced].filter(Boolean).length;

  return (
    <div className={embedded ? 'space-y-4' : 'max-w-7xl mx-auto p-4 md:p-6 space-y-4'}>
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          {!embedded && <h1 className="text-2xl font-bold text-gray-900 dark:text-white inline-flex items-center gap-2"><LibraryBig className="w-6 h-6" /> Asset Library</h1>}
          <p className="text-sm text-gray-500">Every tagged reference the creative brain selects from — search by keyword, filter by type.</p>
        </div>
        <div className="text-sm text-gray-500">{total.toLocaleString()} assets{activeFilters ? ` · ${activeFilters} filter${activeFilters > 1 ? 's' : ''}` : ''}</div>
      </div>

      {/* Search box — type a keyword and it routes to the right asset (name, transcript, state, wardrobe, scene…) */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder='Search — e.g. "texas map", "45 woman kitchen", "senior man", "insurance hook", "walmart supervisor"…'
          className="w-full pl-10 pr-24 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {(q || activeFilters > 0) && (
          <button onClick={clearFilters}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] px-2 py-1 rounded-full border border-border text-gray-500 hover:bg-accent">
            clear
          </button>
        )}
      </div>

      {/* Media type tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {MEDIA_TABS.map(t => (
          <button key={t.id} onClick={() => setMedia(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition inline-flex items-center gap-1.5 ${media === t.id ? 'bg-primary text-white border-primary' : 'border-border text-gray-600 dark:text-gray-300 hover:bg-accent'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
        <div className="w-px h-6 bg-border mx-1" />
        {/* Kind filter */}
        <select value={kind} onChange={e => setKind(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-border bg-card text-sm text-gray-600 dark:text-gray-300">
          {KINDS.map(k => <option key={k} value={k}>{k ? k : 'all kinds'}</option>)}
        </select>
        <select value={gender} onChange={e => setGender(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-border bg-card text-sm text-gray-600 dark:text-gray-300">
          {GENDERS.map(g => <option key={g} value={g}>{g ? g : 'any gender'}</option>)}
        </select>
        <select value={ageBand} onChange={e => setAgeBand(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-border bg-card text-sm text-gray-600 dark:text-gray-300">
          {AGE_BANDS.map(a => <option key={a} value={a}>{a ? a : 'any age'}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 cursor-pointer px-2">
          <input type="checkbox" checked={lipOnly} onChange={e => setLipOnly(e.target.checked)} /> 🗣 lip-sync ready
        </label>
      </div>

      {/* Grid */}
      {loading && !assets.length ? (
        <div className="py-20 text-center text-gray-400 text-sm">Loading…</div>
      ) : !assets.length ? (
        <div className="py-20 text-center text-gray-400 text-sm">No assets match — try clearing filters or a different keyword.</div>
      ) : (
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 ${loading ? 'opacity-50' : ''}`}>
          {assets.map(a => (
            <div key={a.id} className="rounded-lg border border-border overflow-hidden bg-card flex flex-col">
              <AssetMedia url={a.play_url} s3key={a.s3_key} className="w-full aspect-[9/16] object-cover bg-black" />
              <div className="p-2 space-y-0.5 text-[11px] text-gray-500 flex-1">
                <div className="font-medium text-gray-800 dark:text-gray-200 truncate" title={a.name}>{a.name || a.kind}</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-1.5 py-0.5 rounded bg-accent text-[10px]">{a.kind}</span>
                  {a.usable_as === 'avatar_lipsync' && <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 text-[10px]">🗣 lip-sync</span>}
                  {a.state_code && <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-600 text-[10px]">{a.state_code}</span>}
                </div>
                {a.kind === 'avatar' && <div>{a.age_band || '—'} · {a.gender || '—'}{a.face_score ? ` · face ${a.face_score}` : ''}</div>}
                {a.scene ? <div className="truncate" title={a.scene}>{a.scene}</div> : null}
                {a.duration ? <div className="text-gray-400">{Math.round(a.duration)}s{a.has_captions ? ' · captions' : ''}</div> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-border text-sm disabled:opacity-40 hover:bg-accent">← Prev</button>
          <span className="text-sm text-gray-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))}
            className="px-3 py-1.5 rounded-lg border border-border text-sm disabled:opacity-40 hover:bg-accent">Next →</button>
        </div>
      )}
    </div>
  );
}

export default AssetLibraryPage;
